import { fetchMISTableOptions, fetchAuditedTableData } from '../../store/company/service';
import { getTimeStamp } from '../../utils/dateUtils';
import { FINANCIAL_NATURE } from '../../utils/constants/commonConstants';
import type { NatureSignal, SliceRequest, StatementSlice, StatementSlicesResult } from './useAicaReportData.types';
import {
    CASH_FLOW_INDIRECT_MIS,
    CASH_FLOW_INDIRECT_TITLE,
    MIS_TYPE_AUDITED_FINANCIALS,
    MIS_TYPE_PROVISIONAL_STATEMENTS,
} from '../Dashboard/DashboardModules/AuditedFinancialsVisualisation/auditedStatementLayout';
import type { FinancialNature } from '../Dashboard/DashboardModules/AuditedFinancialsVisualisation/auditedTypeUtils';

export const pickData = (res: PromiseSettledResult<any>): any => (res.status === 'fulfilled' ? res.value?.data ?? null : null);

export const pickError = (res: PromiseSettledResult<any>): string | undefined =>
    res.status === 'rejected' ? res.reason?.response?.data?.message || res.reason?.message || 'Request failed' : undefined;

export function findMisOptionsArray(node: any, depth = 0): any[] | null {
    if (!node || depth > 6) return null;
    if (Array.isArray(node)) {
        if (node.some((b: any) => Array.isArray(b?.misOptions))) return node;
        return null;
    }
    if (typeof node !== 'object') return null;
    for (const v of Object.values(node)) {
        const found = findMisOptionsArray(v, depth + 1);
        if (found) return found;
    }
    return null;
}

const INDIRECT_CASH_FLOW_TYPES = new Set([MIS_TYPE_AUDITED_FINANCIALS, MIS_TYPE_PROVISIONAL_STATEMENTS]);

const NATURES = Object.values(FINANCIAL_NATURE);
const NATURE_KEYS = ['latestStandaloneFY', 'latestConsolidatedFY', 'hasStandalone', 'hasConsolidated'];

const blocksHaveNature = (blocks: any[], fyKey: string, flagKey: string): boolean =>
    blocks.some((b) => typeof b?.[fyKey] === 'number' || b?.[flagKey] === true);

export const readNatureSignal = (blocks: any[]): NatureSignal => {
    if (!blocks.some((b) => NATURE_KEYS.some((k) => b?.[k] !== undefined))) return 'unknown';
    return {
        [FINANCIAL_NATURE.STANDALONE]: blocksHaveNature(blocks, 'latestStandaloneFY', 'hasStandalone'),
        [FINANCIAL_NATURE.CONSOLIDATED]: blocksHaveNature(blocks, 'latestConsolidatedFY', 'hasConsolidated'),
    };
};

const payloadHasRows = (payload: any): boolean => ((payload?.data?.data ?? payload?.data)?.rows?.length ?? 0) > 0;
const withRows = (slices: StatementSlice[]) => slices.filter((s) => payloadHasRows(s.payload));

// Each request carries its own name: `enabled[i]` has no entry for the appended indirect request.
const buildSliceRequests = (orgId: string, type: string, enabled: any[]): SliceRequest[] => {
    const requests: SliceRequest[] = enabled.map((o: any) => ({
        name: o.misDisplayName,
        payload: { orgId, requestId: getTimeStamp(), type, mis: o.misDisplayName, raw: false },
    }));
    if (INDIRECT_CASH_FLOW_TYPES.has(type)) {
        requests.push({
            name: CASH_FLOW_INDIRECT_TITLE,
            payload: {
                orgId,
                requestId: getTimeStamp(),
                type,
                mis: CASH_FLOW_INDIRECT_MIS,
                raw: false,
                ...(type === MIS_TYPE_PROVISIONAL_STATEMENTS ? { tenure: 'Yearly' } : {}),
            },
        });
    }
    return requests;
};

const settleRequests = async (requests: SliceRequest[]) => {
    const settled = await Promise.allSettled(requests.map((r) => fetchAuditedTableData(r.payload)));
    return settled.map((res, i) => {
        const payload = res.status === 'fulfilled' ? res.value?.data ?? null : null;
        return { slice: payload ? { name: requests[i].name, payload } : null, error: pickError(res) };
    });
};

const fetchNaturelessSlices = async (requests: SliceRequest[]) => {
    const results = await settleRequests(requests);
    return {
        slices: results.flatMap((r) => (r.slice ? [r.slice] : [])),
        errors: results.flatMap((r) => (r.error ? [r.error] : [])),
    };
};

const fetchNaturelessResult = async (
    requests: SliceRequest[],
    reportErrors: boolean,
    errors: string[] = [],
): Promise<StatementSlicesResult | undefined> => {
    const fallback = await fetchNaturelessSlices(requests);
    if (withRows(fallback.slices).length > 0) return { slices: fallback.slices, errors: [] };
    const fallbackErrors = reportErrors ? fallback.errors : [];
    return fallbackErrors.length > 0 ? { errors: [...errors, ...fallbackErrors] } : undefined;
};

// Only confirmed versions are fetched; with no signal, or no rows from any version, it is today's single nature-less fetch.
const fetchSlicesByNature = async (requests: SliceRequest[], optionsArray: any[]): Promise<StatementSlicesResult | undefined> => {
    const signal = readNatureSignal(optionsArray);
    if (signal === 'unknown') return fetchNaturelessResult(requests, false);

    const natureRequests = NATURES.filter((n) => signal[n]).flatMap((nature) =>
        requests.map((r) => ({ nature, request: { ...r, payload: { ...r.payload, nature } } })),
    );
    const results = await settleRequests(natureRequests.map((n) => n.request));

    const byNature: Partial<Record<FinancialNature, StatementSlice[]>> = {};
    const errors: string[] = [];
    results.forEach((res, i) => {
        const { nature } = natureRequests[i];
        if (res.slice) (byNature[nature] ??= []).push(res.slice);
        if (res.error) errors.push(res.error);
    });
    NATURES.forEach((n) => {
        if (byNature[n] && withRows(byNature[n]!).length === 0) delete byNature[n];
    });
    if (Object.keys(byNature).length > 0) return { byNature, errors };

    return fetchNaturelessResult(
        requests,
        NATURES.some((n) => signal[n]),
        errors,
    );
};

export async function fetchStatementSlices(
    orgId: string,
    source: string,
    type: string,
    optionsType?: string,
    { byNature = false }: { byNature?: boolean } = {},
): Promise<StatementSlicesResult | undefined> {
    let optionsRes: any;
    try {
        optionsRes = await fetchMISTableOptions(optionsType ? { orgId, source, type: optionsType } : { orgId, source });
    } catch {
        return undefined;
    }
    const optionsArray = findMisOptionsArray(optionsRes?.data) ?? [];
    const misOptionsBlock = optionsArray.find((b: any) => Array.isArray(b?.misOptions));
    const misOptions: any[] = misOptionsBlock?.misOptions ?? [];
    const enabled = misOptions.filter((o: any) => o?.misDisplayName && o?.isDataAvailable !== false);
    if (enabled.length === 0 && !INDIRECT_CASH_FLOW_TYPES.has(type)) return undefined;

    const requests = buildSliceRequests(orgId, type, enabled);
    if (byNature) return fetchSlicesByNature(requests, optionsArray);

    const { slices } = await fetchNaturelessSlices(requests);
    return slices.length === 0 ? undefined : { slices };
}
